import { useCallback, useContext, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { parseAsString, useQueryState } from 'nuqs';
import { AuthContext } from '@@/contexts/AuthContext';
import { CheckCircle, XCircle, AlertTriangle, ArrowLeftIcon } from 'lucide-react';
import { PageHeader } from '@@/ui/components';
import { Badge, Button } from '@@/ui/primitives';
import { DataTable, type ColumnDef } from '../components/shared/data-table';
import { FilterPanel, type FilterSection } from '../components/shared/filter-panel';
import { CampaignType } from '../types';
import type { AgencyOverview as AgencyOverviewType, Campaign } from '../types';

const mockAgencyData: Record<string, AgencyOverviewType> = {
  '1': {
    name: 'Agency A',
    nextCreditRenew: '11/05/2025',
    campaigns: { used: 800, limit: 1000 },
    smsMessages: { used: 1850, limit: 1800 },
    voiceMessages: { used: 500, limit: 700 }
  },
  '2': {
    name: 'Agency B',
    nextCreditRenew: '11/05/2025',
    campaigns: { used: 600, limit: 1000 },
    smsMessages: { used: 1200, limit: 1800 },
    voiceMessages: { used: 400, limit: 700 }
  },
  '3': {
    name: 'Agency C',
    nextCreditRenew: '11/05/2025',
    campaigns: { used: 900, limit: 1000 },
    smsMessages: { used: 1600, limit: 1800 },
    voiceMessages: { used: 650, limit: 700 }
  },
  '4': {
    name: 'Agency D',
    nextCreditRenew: '11/05/2025',
    campaigns: { used: 750, limit: 1000 },
    smsMessages: { used: 1400, limit: 1800 },
    voiceMessages: { used: 550, limit: 700 }
  },
  '5': {
    name: 'Agency E',
    nextCreditRenew: '11/05/2025',
    campaigns: { used: 500, limit: 1000 },
    smsMessages: { used: 1000, limit: 1800 },
    voiceMessages: { used: 300, limit: 700 }
  }
};

// const mockSurveyStats: SurveyStats = {
//   smsSent: 6893,
//   voiceCampaignDuration: '3h47m:21s',
//   deliveryRate: 83.2,
//   campaignsOpenRate: 42.7,
//   surveySubmissionRate: 67.6,
//   optOut: 7.1
// };

const mockCampaigns: Campaign[] = [
  {
    id: '1',
    name: 'Community Feedback Survey',
    type: CampaignType.SURVEY_VOICE,
    active: true,
    credits: 1200,
    responseRate: 68,
    deliveryRate: 68
  },
  {
    id: '2',
    name: 'Emergency Alert - Weather',
    type: CampaignType.NOTIFICATION_VOICE,
    active: true,
    credits: 800,
    responseRate: null,
    deliveryRate: null
  },
  {
    id: '3',
    name: 'Service Update SMS',
    type: CampaignType.NOTIFICATION_SMS,
    active: false,
    credits: 500,
    responseRate: null,
    deliveryRate: null
  },
  {
    id: '4',
    name: 'Resident Satisfaction Survey',
    type: CampaignType.SURVEY_SMS,
    active: false,
    credits: 1000,
    responseRate: 72,
    deliveryRate: 72
  },
  {
    id: '5',
    name: 'Utility Maintenance Notice',
    type: CampaignType.NOTIFICATION_VOICE,
    active: false,
    credits: 800,
    responseRate: null,
    deliveryRate: null
  }
];

const campaignTypeLabels: Record<CampaignType, string> = {
  [CampaignType.SURVEY_VOICE]: 'Voice Survey',
  [CampaignType.SURVEY_SMS]: 'SMS Survey',
  [CampaignType.NOTIFICATION_VOICE]: 'Voice Notification',
  [CampaignType.NOTIFICATION_SMS]: 'SMS Notification'
};

const getTypeBadgeStyles = (type: CampaignType) => {
  switch (type) {
    case CampaignType.SURVEY_VOICE:
    case CampaignType.NOTIFICATION_VOICE:
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case CampaignType.SURVEY_SMS:
    case CampaignType.NOTIFICATION_SMS:
      return 'bg-green-50 text-green-700 border-green-200';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const agencyFilterSections: FilterSection[] = [
  {
    key: 'type',
    label: 'Type',
    options: [
      { value: CampaignType.SURVEY_VOICE, label: 'Voice Survey' },
      { value: CampaignType.SURVEY_SMS, label: 'SMS Survey' },
      { value: CampaignType.NOTIFICATION_VOICE, label: 'Voice Notification' },
      { value: CampaignType.NOTIFICATION_SMS, label: 'SMS Notification' }
    ]
  },
  {
    key: 'active',
    label: 'Status',
    options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' }
    ]
  }
];

const AgencyPageContent = () => {
  const { agencyId } = useParams<{ agencyId: string }>();
  const navigate = useNavigate();
  const { getPathWithInstanceAlias } = useContext(AuthContext);

  const [searchTerm, setSearchTerm] = useQueryState('search', parseAsString.withDefault(''));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string>>>({
    type: new Set(),
    active: new Set()
  });

  const agencyData = agencyId ? mockAgencyData[agencyId] : null;

  const handleSelectionChange = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === mockCampaigns.length) {
        return new Set();
      }
      return new Set(mockCampaigns.map((c) => c.id));
    });
  }, []);

  const handleFilterChange = useCallback((sectionKey: string, value: string) => {
    setSelectedFilters((prev) => {
      const newSet = new Set(prev[sectionKey]);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return { ...prev, [sectionKey]: newSet };
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedFilters({
      type: new Set(),
      active: new Set()
    });
  }, [setSearchTerm]);

  const handleApplyFilters = useCallback(() => {
    // Filters are applied automatically via filteredCampaigns
  }, []);

  const handleBackToAllAgencies = useCallback(() => {
    navigate(getPathWithInstanceAlias('/portal/campaigns/'));
  }, [navigate, getPathWithInstanceAlias]);

  if (!agencyData) {
    return (
      <div className='flex flex-col h-full'>
        <PageHeader title='Campaigns Dashboard' description='Track your agencies and campaigns performances' />
        <div className='w-full px-10 py-8 flex-1'>
          <p>Agency not found</p>
          <button onClick={handleBackToAllAgencies} className='text-primary hover:underline text-sm mt-4'>
            ← Back to All Agencies
          </button>
        </div>
      </div>
    );
  }

  const filteredCampaigns = useMemo(() => {
    const { type, active } = selectedFilters;

    return mockCampaigns.filter((c) => {
      const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = type.size === 0 || type.has(c.type);
      const matchesActive = active.size === 0 || active.has(String(c.active));

      return matchesSearch && matchesType && matchesActive;
    });
  }, [searchTerm, selectedFilters]);

  const campaignColumns: ColumnDef<Campaign>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Campaign Name',
        headerClassName: 'text-xs font-semibold text-foreground',
        render: (campaign) => (
          <a href='#' className='text-sm text-primary hover:underline'>
            {campaign.name}
          </a>
        )
      },
      {
        key: 'type',
        header: 'Type',
        headerClassName: 'text-xs font-semibold text-foreground',
        render: (campaign) => (
          <Badge variant='outline' className={getTypeBadgeStyles(campaign.type)}>
            {campaignTypeLabels[campaign.type]}
          </Badge>
        )
      },
      {
        key: 'active',
        header: 'Active',
        headerClassName: 'text-xs font-semibold text-foreground text-center',
        cellClassName: 'text-center',
        render: (campaign) =>
          campaign.active ? (
            <CheckCircle className='h-5 w-5 text-green-500 inline-block' />
          ) : (
            <XCircle className='h-5 w-5 text-red-500 inline-block' />
          )
      },
      {
        key: 'credits',
        header: 'Credits',
        headerClassName: 'text-xs font-semibold text-foreground text-right',
        cellClassName: 'text-right text-sm',
        render: (campaign) => (
          <span className='flex items-center justify-end gap-1'>
            {campaign.credits.toLocaleString()}
            {campaign.credits > 1000 && <AlertTriangle className='h-4 w-4 text-orange-500' />}
          </span>
        )
      },
      {
        key: 'responseRate',
        header: 'Response Rate',
        headerClassName: 'text-xs font-semibold text-foreground text-right',
        cellClassName: 'text-right text-sm',
        render: (campaign) => (campaign.responseRate !== null ? `${campaign.responseRate}%` : 'N/A')
      },
      {
        key: 'deliveryRate',
        header: 'Delivery Rate',
        headerClassName: 'text-xs font-semibold text-foreground text-right pr-4',
        cellClassName: 'text-right text-sm pr-4',
        render: (campaign) => (campaign.deliveryRate !== null ? `${campaign.deliveryRate}%` : 'N/A')
      }
    ],
    []
  );

  return (
    <div className='flex flex-col h-full'>
      <PageHeader title='Campaigns Dashboard' description='Track your agencies and campaigns performances' />

      <div className='w-full px-10 py-6 flex-1 space-y-4 overflow-y-auto'>
        <div className='flex items-center gap-4'>
          <Button variant='outline' onClick={handleBackToAllAgencies}>
            <ArrowLeftIcon className='size-4' />
            Agency Overview
          </Button>
        </div>
        {/*
        <AgencyOverview data={agencyData} />

        <SurveyResponsesGrid stats={mockSurveyStats} /> */}

        <div className='space-y-4'>
          <h3 className='text-xl font-semibold text-foreground'>Campaigns Performance</h3>
          <div className='flex gap-6'>
            <FilterPanel
              sections={agencyFilterSections}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              onApplyFilters={handleApplyFilters}
            />
            <DataTable
              title='Campaigns'
              data={filteredCampaigns}
              totalCount={mockCampaigns.length}
              columns={campaignColumns}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder='Search campaigns by name'
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
              onSelectAll={handleSelectAll}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AgencyPage = AgencyPageContent;
